import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BASE, authFetch } from '../constants';

/**
 * SSO Callback Page — processes the redirect from IdP.
 * For OIDC: receives ?code=...&state=... and exchanges for tokens.
 * For SAML: the POST callback is handled server-side; this page handles
 * the frontend redirect after server processes the SAML response.
 */
export default function SSOCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const token = searchParams.get('token');

    if (errorParam) {
      setError(searchParams.get('error_description') || errorParam);
      setProcessing(false);
      return;
    }

    // If token is already in URL (server-side redirect after SAML), store and redirect
    if (token) {
      localStorage.setItem('access_token', token);
      const refreshToken = searchParams.get('refresh_token');
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      navigate('/');
      return;
    }

    // OIDC callback — exchange code for tokens
    if (code && state) {
      authFetch(`${BASE}/api/v1/sso/oidc/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`)
        .then(resp => resp.json())
        .then(data => {
          if (data.error) {
            setError(data.error || data.detail);
            setProcessing(false);
          } else if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
            if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
            navigate('/');
          } else {
            setError('Unexpected response from SSO callback');
            setProcessing(false);
          }
        })
        .catch(err => {
          setError(err.message);
          setProcessing(false);
        });
    } else {
      setError('Missing authorization code or state parameter');
      setProcessing(false);
    }
  }, [searchParams, navigate]);

  if (processing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Processing SSO login...</div>
        <div style={{ color: '#64748b' }}>Please wait while we verify your identity.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
      <div style={{ padding: 24, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, maxWidth: 500, textAlign: 'center' }}>
        <h3 style={{ color: '#dc2626', marginTop: 0 }}>SSO Login Failed</h3>
        <p style={{ color: '#64748b' }}>{error}</p>
        <button onClick={() => navigate('/')}
          style={{ padding: '8px 20px', background: 'var(--navy-800)', color: 'var(--text-primary)', border: 'none', borderRadius: 6, cursor: 'pointer', marginTop: 12 }}>
          Return to Dashboard
        </button>
      </div>
    </div>
  );
}
